import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, TextField,
  Button, Switch, FormControlLabel, MenuItem, Divider, InputAdornment,
} from "@mui/material";
import { toast } from "react-toastify";
import { createTenant } from "./api/tenantApi";

const BLUE = "#1976d2";

const AI_ENGINES = [
  { value: "default", label: "Default" },
  { value: "gemini", label: "CrediArc AI — Standard" },
  { value: "claude", label: "CrediArc AI — Advanced" },
];

export default function CreateTenantModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", maxCounterLimit: 1000, aiEngine: "default",
    tenantType: "regular", assessmentCategory: "trade_credit",
    isPaid: false, isAREnabled: false, isDNBSearchEnabled: false,
    isDNBFinancialDataEnabled: false, isCustomPromptsEnabled: false,
    isInsuranceEnabled: false, isArPlusEnabled: false,
  });
  const [saving, setSaving] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const reset = () => setForm({
    name: "", email: "", maxCounterLimit: 1000, aiEngine: "default",
    tenantType: "regular", assessmentCategory: "trade_credit",
    isPaid: false, isAREnabled: false, isDNBSearchEnabled: false,
    isDNBFinancialDataEnabled: false, isCustomPromptsEnabled: false,
    isInsuranceEnabled: false, isArPlusEnabled: false,
  });

  const handleClose = () => { if (!saving) { reset(); onClose(); } };

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error("Tenant name is required");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        emailList: form.email.trim() ? [form.email.trim()] : [],
        isPaid: form.isPaid,
        isAREnabled: form.isAREnabled,
        maxCounterLimit: Number(form.maxCounterLimit) || 1000,
        aiEngine: form.aiEngine,
        tenantType: form.tenantType,
        assessmentCategory: form.tenantType === "payment_platform" ? "mca_loan" : form.assessmentCategory,
        paymentPlatformConfig: form.tenantType === "payment_platform" ? {
          sparseIdentityAllowed: true,
          dnbRequired: false,
          entityResolution: "multi_identifier",
          modelIsolation: "per_tenant",
        } : null,
        isDNBSearchEnabled: form.isDNBSearchEnabled,
        isDNBFinancialDataEnabled: form.isDNBFinancialDataEnabled,
        isCustomPromptsEnabled: form.isCustomPromptsEnabled,
        isInsuranceEnabled: form.isInsuranceEnabled,
        isArPlusEnabled: form.isArPlusEnabled,
      };
      const res = await createTenant(payload);
      const created = res?.data?.id ? res.data : (res?.data?.data || res?.data || null);
      toast.success(`Tenant "${form.name}" created`);
      reset();
      onCreated && onCreated(created);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to create tenant");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key, label, hint) => (
    <FormControlLabel
      sx={{ alignItems: "flex-start", m: 0, mb: 1 }}
      control={<Switch checked={form[key]} onChange={(e) => set({ [key]: e.target.checked })} sx={{ mt: -0.5 }} />}
      label={
        <Box>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a" }}>{label}</Typography>
          {hint && <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{hint}</Typography>}
        </Box>
      }
    />
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Create a new tenant</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "#94a3b8", mb: 1 }}>Details</Typography>
        <TextField label="Tenant name" value={form.name} onChange={(e) => set({ name: e.target.value })} fullWidth margin="dense" size="small" autoFocus />
        <TextField label="Primary contact email" value={form.email} onChange={(e) => set({ email: e.target.value })} fullWidth margin="dense" size="small"
          helperText="Used as the tenant's main contact and added to its email list." />
        <TextField
          select
          label="Tenant type"
          value={form.tenantType}
          onChange={(e) => {
            const tenantType = e.target.value;
            set({
              tenantType,
              assessmentCategory: tenantType === "payment_platform" ? "mca_loan" : "trade_credit",
              ...(tenantType === "payment_platform" ? { isDNBSearchEnabled: false, isDNBFinancialDataEnabled: false } : {}),
            });
          }}
          fullWidth
          margin="dense"
          size="small"
          helperText={form.tenantType === "payment_platform" ? "Sparse merchant identity, tenant-specific internal data, and MCA / loan recommendations." : "Standard trade-credit and AR assessment workflow."}
        >
          <MenuItem value="regular">Regular credit tenant</MenuItem>
          <MenuItem value="payment_platform">Payment platform · MCA / loans</MenuItem>
        </TextField>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mt: 1 }}>
          <TextField label="Max counterparties / period" type="number" value={form.maxCounterLimit}
            onChange={(e) => set({ maxCounterLimit: e.target.value })} size="small"
            InputProps={{ endAdornment: <InputAdornment position="end">cos</InputAdornment> }} />
          <TextField select label="AI engine" value={form.aiEngine} onChange={(e) => set({ aiEngine: e.target.value })} size="small">
            {AI_ENGINES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "#94a3b8", mb: 1 }}>Plan & features</Typography>
        {toggle("isPaid", "Paid account", "Otherwise treated as a trial.")}
        {toggle("isAREnabled", "Accounts Receivable", "Enable the AR portfolio module.")}
        {form.tenantType !== "payment_platform" && toggle("isDNBSearchEnabled", "CrediArc company search")}
        {form.tenantType !== "payment_platform" && toggle("isDNBFinancialDataEnabled", "CrediArc financial data")}
        {toggle("isCustomPromptsEnabled", "Custom prompts")}
        {toggle("isInsuranceEnabled", "Insurance", "Show the credit-insurance layer to this tenant.")}
        {toggle("isArPlusEnabled", "AR+ (risk & collections)", "Expanded AR ingestion, pivots, stress testing & collections.")}

        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: "10px", background: "#f1f5f9" }}>
          <Typography sx={{ fontSize: 12.5, color: "#475569" }}>
            After creating the tenant, use <strong>Manage users</strong> to add people — each gets a login account.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" disabled={saving} sx={{ textTransform: "none", background: BLUE }}>
          {saving ? "Creating…" : "Create tenant"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
